import {
  type Dispatch,
  type MutableRefObject,
  type PointerEvent as ReactPointerEvent,
  type SetStateAction,
  useCallback,
  useEffect,
} from 'react'
import type { ShapeManagerRef } from '@/lib/shapes/types/shape-manager-ref'
import { isMiddleButtonPanPointerStart } from '@/lib/zoom/editor-canvas-zoom-pointer'
import { areGuideSettingsDialogShortcutsDisabled } from '@/lib/keyboard/dialog-shortcuts'
import { shouldSpaceGoToFocusedElement } from '@/lib/editor/space-key-target'
import { getMaxScrollOffsets } from '../helpers/clamp-scroll'
import type { PanDragState } from '../types/pan-drag-state'

/** Snapshot of a pan drag's start: pointer, scroll position and which gesture started it. */
function buildPanDragState(
  element: HTMLDivElement,
  e: ReactPointerEvent<HTMLDivElement>,
  scroller: HTMLDivElement,
  source: PanDragState['source']
): PanDragState {
  return {
    element,
    pointerId: e.pointerId,
    startClientX: e.clientX,
    startClientY: e.clientY,
    startScrollLeft: scroller.scrollLeft,
    startScrollTop: scroller.scrollTop,
    source,
  }
}

/**
 * Canvas panning: clamped scroll writes, hand-tool / Space and middle-button pan-drag start, the
 * pan drag's pointer move/up, pan drag teardown, and the Space-to-pan key listeners. Pan state
 * lives in `use-canvas-pan-state`; `use-pasteboard-pointer` routes the pasteboard's pointer
 * events here.
 */
export function useCanvasPan({
  scrollRef,
  internalShapeManagerRef,
  panDragRef,
  panFrameRef,
  panScrollTargetRef,
  setIsDragPanning,
  setIsSpacePanActive,
  isPanInteractionActive,
  isEditableTarget,
  syncRulerScrollTransforms,
}: {
  scrollRef: MutableRefObject<HTMLDivElement | null>
  internalShapeManagerRef: MutableRefObject<ShapeManagerRef | null>
  panDragRef: MutableRefObject<PanDragState | null>
  panFrameRef: MutableRefObject<number | null>
  panScrollTargetRef: MutableRefObject<{ left: number; top: number } | null>
  setIsDragPanning: Dispatch<SetStateAction<boolean>>
  setIsSpacePanActive: Dispatch<SetStateAction<boolean>>
  isPanInteractionActive: boolean
  isEditableTarget: (target: EventTarget | null) => boolean
  syncRulerScrollTransforms: () => void
}) {
  const applyPanScroll = useCallback(
    (left: number, top: number) => {
      const scroller = scrollRef.current

      if (!scroller) {
        return { left: 0, top: 0, hitEdges: [] as Array<'top' | 'right' | 'bottom' | 'left'> }
      }

      const { maxLeft, maxTop } = getMaxScrollOffsets(scroller)
      const nextLeft = Math.max(0, Math.min(maxLeft, left))
      const nextTop = Math.max(0, Math.min(maxTop, top))
      const hitEdges: Array<'top' | 'right' | 'bottom' | 'left'> = []

      if (left < 0 || (nextLeft === 0 && left !== nextLeft)) hitEdges.push('left')
      if (left > maxLeft || (nextLeft === maxLeft && left !== nextLeft)) hitEdges.push('right')
      if (top < 0 || (nextTop === 0 && top !== nextTop)) hitEdges.push('top')
      if (top > maxTop || (nextTop === maxTop && top !== nextTop)) hitEdges.push('bottom')

      scroller.scrollLeft = nextLeft
      scroller.scrollTop = nextTop
      syncRulerScrollTransforms()

      return { left: nextLeft, top: nextTop, hitEdges }
    },
    [syncRulerScrollTransforms, scrollRef]
  )

  const endPanDrag = useCallback(() => {
    const panDrag = panDragRef.current

    if (panDrag?.element.hasPointerCapture(panDrag.pointerId)) {
      panDrag.element.releasePointerCapture(panDrag.pointerId)
    }

    if (panFrameRef.current != null) {
      cancelAnimationFrame(panFrameRef.current)
      panFrameRef.current = null
    }

    panDragRef.current = null
    panScrollTargetRef.current = null
    setIsDragPanning(false)
  }, [panDragRef, panFrameRef, panScrollTargetRef, setIsDragPanning])

  useEffect(() => {
    if (isPanInteractionActive) return
    if (!panDragRef.current) return
    if (panDragRef.current.source === 'middle') return
    endPanDrag()
  }, [endPanDrag, isPanInteractionActive, panDragRef])

  useEffect(() => {
    const isSpacePanKey = (e: KeyboardEvent) =>
      e.code === 'Space' || e.key === ' ' || e.key === 'Spacebar'

    const onKeyDown = (e: KeyboardEvent) => {
      if (!isSpacePanKey(e)) return
      if (areGuideSettingsDialogShortcutsDisabled()) return
      if (isEditableTarget(e.target)) return
      // A keyboard-focused control (Tab-reached button, switch, option, thumbnail…) gets Space
      // as its own "activate" key, like Enter; a mouse-clicked one still pans. App-wide.
      if (shouldSpaceGoToFocusedElement(e.target)) return
      if (e.ctrlKey || e.metaKey || e.altKey) return
      if (document.querySelector('[data-editor-context-menu]')) return

      e.preventDefault()
      e.stopPropagation()
      if (e.repeat) return
      setIsSpacePanActive(true)
    }

    const onKeyUp = (e: KeyboardEvent) => {
      if (!isSpacePanKey(e)) return
      if (isEditableTarget(e.target)) return
      setIsSpacePanActive(false)
      // Browsers fire a button's Space click on keyup, so this must let it through too.
      if (shouldSpaceGoToFocusedElement(e.target)) return
      e.preventDefault()
      e.stopPropagation()
    }

    const onBlur = () => {
      setIsSpacePanActive(false)
    }

    window.addEventListener('keydown', onKeyDown, true)
    window.addEventListener('keyup', onKeyUp, true)
    window.addEventListener('blur', onBlur)

    return () => {
      window.removeEventListener('keydown', onKeyDown, true)
      window.removeEventListener('keyup', onKeyUp, true)
      window.removeEventListener('blur', onBlur)
    }
  }, [isEditableTarget, setIsSpacePanActive])

  const handlePanPointerDown = useCallback(
    (e: ReactPointerEvent<HTMLDivElement>) => {
      const scroller = scrollRef.current
      const mgr = internalShapeManagerRef.current

      if (!isPanInteractionActive) return false
      if (e.button !== 0) return false
      if (!scroller) return false
      if (mgr?.isInteracting()) return false

      e.preventDefault()
      e.currentTarget.setPointerCapture(e.pointerId)
      panDragRef.current = buildPanDragState(e.currentTarget, e, scroller, 'moded')
      setIsDragPanning(true)
      return true
    },
    [isPanInteractionActive, scrollRef, internalShapeManagerRef, panDragRef, setIsDragPanning]
  )

  /** Middle mouse button: pan like the hand tool without toggling pan mode (cursor grab while dragging). */
  const handleMiddlePanPointerDown = useCallback(
    (e: ReactPointerEvent<HTMLDivElement>, captureElement: HTMLDivElement | null = null) => {
      if (!isMiddleButtonPanPointerStart(e)) return false

      const scroller = scrollRef.current
      const mgr = internalShapeManagerRef.current

      if (!scroller) return false
      if (mgr?.isInteracting()) return false

      const el = captureElement ?? e.currentTarget

      e.preventDefault()
      e.stopPropagation()
      el.setPointerCapture(e.pointerId)
      panDragRef.current = buildPanDragState(el, e, scroller, 'middle')
      setIsDragPanning(true)
      return true
    },
    [scrollRef, internalShapeManagerRef, panDragRef, setIsDragPanning]
  )

  /** Pan-drag pointer move: scroll toward the dragged position, one write per frame. */
  const handlePanDragPointerMove = useCallback(
    (e: ReactPointerEvent<HTMLDivElement>): boolean => {
      const panDrag = panDragRef.current

      if (!panDrag || e.pointerId !== panDrag.pointerId) return false

      e.preventDefault()
      const nextLeft = panDrag.startScrollLeft - (e.clientX - panDrag.startClientX)
      const nextTop = panDrag.startScrollTop - (e.clientY - panDrag.startClientY)

      panScrollTargetRef.current = { left: nextLeft, top: nextTop }

      if (panFrameRef.current == null) {
        panFrameRef.current = requestAnimationFrame(() => {
          panFrameRef.current = null
          const next = panScrollTargetRef.current

          if (!next) return
          applyPanScroll(next.left, next.top)
        })
      }

      return true
    },
    [applyPanScroll, panDragRef, panFrameRef, panScrollTargetRef]
  )

  /** Pan-drag pointer up: end the drag. */
  const handlePanDragPointerUp = useCallback(
    (e: ReactPointerEvent<HTMLDivElement>): boolean => {
      const panDrag = panDragRef.current

      if (!panDrag || e.pointerId !== panDrag.pointerId) return false

      e.preventDefault()
      endPanDrag()
      return true
    },
    [endPanDrag, panDragRef]
  )

  return {
    applyPanScroll,
    endPanDrag,
    handlePanPointerDown,
    handleMiddlePanPointerDown,
    handlePanDragPointerMove,
    handlePanDragPointerUp,
  }
}
